import { GlobalTab } from 'src/utils/hooks';
import { create, StateCreator } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// PersistedTab defines subset of fields which is persisted to sessionStorage.
type PersistedTab = Omit<GlobalTab, 'next' | 'prev'>;

export type TabsStoreState = {
  tabs: GlobalTab[];
  addTab: (tab: GlobalTab) => void;
  removeTab: (id: string) => void;
  activeTab: GlobalTab | null;
  setActiveTab: (tab: GlobalTab | null) => void;
};

export const createTabsSlice: StateCreator<TabsStoreState> = (setState) => ({
  tabs: [],
  activeTab: null,
  addTab: (tab: GlobalTab) => {
    setState((state) => ({
      tabs: [...state.tabs, tab],
    }));
  },
  removeTab: (id: string) => {
    setState((state) => ({
      tabs: state.tabs.filter((tab) => tab.id !== id),
    }));
  },
  setActiveTab: (tab: GlobalTab | null) => {
    setState(() => ({
      activeTab: tab,
    }));
  },
});

const useTabsStore = create<TabsStoreState>()(
  persist(
    (...args) => ({
      ...createTabsSlice(...args),
    }),
    {
      name: 'portal-tabs-store',
      storage: createJSONStorage(() => sessionStorage),
      // avoid persisting information about linked prev, next tabs.
      partialize: ({ activeTab, tabs }) => {
        let persistedActiveTab: PersistedTab | null = null;
        if (activeTab) {
          const { prev, next, ...rest } = activeTab;
          persistedActiveTab = rest;
        }
        return {
          activeTab: persistedActiveTab,
          tabs: tabs.map(({ next, prev, ...rest }) => rest),
        };
      },
    },
  ),
);

export default useTabsStore;
